import { Controller } from "@hotwired/stimulus"
import { api } from "../composables/useApi"

// Live handle availability check. Debounced GET to /api/users/handle_available
// renders a check/cross icon inside the .pf-input--prefixed wrapper and writes
// the descriptive reason into the helper line below. Server-side validation is
// still authoritative on submit.
export default class extends Controller {
  static targets = ["input", "status", "helper"]
  static values = {
    url: String,
    debounce: { type: Number, default: 350 },
    defaultMessage: { type: String, default: "" },
  }

  connect() {
    this.timer = null
    this.lastChecked = null
    this.requestSeq = 0
    if (this.hasInputTarget && this.inputTarget.value.trim().length > 0) {
      this.fetchAvailability(this.inputTarget.value.trim())
    }
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  check() {
    clearTimeout(this.timer)
    const value = this.inputTarget.value.trim()
    if (value === this.lastChecked) return
    this.renderStatus(null)
    if (value.length === 0) return
    this.timer = setTimeout(() => this.fetchAvailability(value), this.debounceValue)
  }

  async fetchAvailability(value) {
    const seq = ++this.requestSeq
    try {
      const payload = await api.get(this.urlValue, { params: { handle: value } })
      if (seq !== this.requestSeq) return // a newer request superseded this one
      this.lastChecked = value
      this.renderStatus(payload)
    } catch {
      // Error — leave the suffix empty rather than misleading the user.
      if (seq === this.requestSeq) this.renderStatus(null)
    }
  }

  renderStatus(payload) {
    if (!this.hasStatusTarget) return

    const status = this.statusTarget
    const helper = this.hasHelperTarget ? this.helperTarget : null
    status.classList.remove("pf-input__suffix--ok", "pf-input__suffix--err")
    if (helper) helper.classList.remove("pf-help--error", "pf-help--success")

    if (payload === null) {
      status.innerHTML = ""
      if (helper) helper.textContent = this.defaultMessageValue
      return
    }

    if (payload.available) {
      status.classList.add("pf-input__suffix--ok")
      status.innerHTML = '<i class="fas fa-check" aria-hidden="true"></i>'
      if (helper) {
        helper.classList.add("pf-help--success")
        helper.textContent = "Available"
      }
      return
    }

    status.classList.add("pf-input__suffix--err")
    status.innerHTML = '<i class="fas fa-xmark" aria-hidden="true"></i>'
    if (helper) {
      helper.classList.add("pf-help--error")
      helper.textContent = this.reasonMessage(payload.reason)
    }
  }

  reasonMessage(reason) {
    switch (reason) {
      case "taken":    return "That handle is already taken."
      case "reserved": return "That handle is reserved."
      case "blocked":  return "That handle isn't allowed."
      case "format":   return "Use letters, numbers, and underscores only — start with a letter."
      case "length":   return "Handles must be 3–30 characters."
      case "blank":    return this.defaultMessageValue
      default:         return "That handle isn't available."
    }
  }
}
